import { Navigate } from 'react-router';
import { Building2, Contact } from 'lucide-react';
import type { ModuloWeb } from '../../nucleo/modulos-web';
import { Classificacao } from './Classificacao';
import { FichaParceiro } from './FichaParceiro';
import { ListaParceiros } from './ListaParceiros';
import { Projetos } from './Projetos';
import { CondicoesPagamento, FormasPagamento } from './Pagamentos';
import { Servicos } from './Servicos';
import { ServicosContratados } from './ServicosContratados';
import { buscaCadastros } from './busca-global';
import { i18n } from './i18n';
import { Contatos } from './CrmContatos';
import { Empresas } from './CrmEmpresas';
import { criacoesCadastros } from './criacoes';
import { FormularioParceiro } from './FormularioParceiro';

/**
 * Fase 2 (W5, L3): as abas Empresas, Clientes, Fornecedores e Contatos viraram filtros da aba "Pessoas e empresas". As rotas
 * antigas continuam valendo (links salvos, provas, indicador do Início) e levam à aba nova com o filtro certo.
 */
const ROTAS_ANTIGAS = { empresas: 'empresa', clientes: 'cliente', fornecedores: 'fornecedor', contatos: 'contato' } as const;

export const redirecionarAbaAntiga = (filtro: string) => `/cadastros/cadastros/pessoas?papel=${filtro}`;

function IrParaEmpresas() {
  return <Navigate to={redirecionarAbaAntiga(ROTAS_ANTIGAS.empresas)} replace />;
}
function IrParaClientes() {
  return <Navigate to={redirecionarAbaAntiga(ROTAS_ANTIGAS.clientes)} replace />;
}
function IrParaFornecedores() {
  return <Navigate to={redirecionarAbaAntiga(ROTAS_ANTIGAS.fornecedores)} replace />;
}
function IrParaContatos() {
  return <Navigate to={redirecionarAbaAntiga(ROTAS_ANTIGAS.contatos)} replace />;
}

const modulo: ModuloWeb = {
  slug: 'cadastros',
  menu: 'cadastros',
  ordem: 10,
  // Fase 2 (W31, 5.1 a 5.5): grupos do menu Cadastros (Cadastros, Serviços, Financeiro, Agenda, Estoque - Produtos) e a
  // posição de cada aba neles (`NAVEGACAO_MENUS`); os caminhos não mudam.
  abas: [
    { chave: 'pessoas', permissao: 'cadastros.ver', componente: ListaParceiros, grupo: 'cadastros', posicao: 10 },
    { chave: 'projetos', permissao: 'cadastros.ver', componente: Projetos, grupo: 'cadastros', posicao: 20 },
    { chave: 'classificacao', permissao: 'cadastros.ver', componente: Classificacao, grupo: 'cadastros', posicao: 30 },
    // Fase 2 (W6, L12 e L13): catálogos únicos do Cadastros. W31: o catálogo (o que a empresa vende) e os serviços contratados
    // de terceiros (cadastro novo) formam o grupo Serviços.
    { chave: 'servicos', permissao: 'cadastros.ver', componente: Servicos, grupo: 'servicos', posicao: 40 },
    { chave: 'servicos-contratados', permissao: 'cadastros.ver', componente: ServicosContratados, grupo: 'servicos', posicao: 50 },
    { chave: 'formas-pagamento', permissao: 'cadastros.ver', componente: FormasPagamento, grupo: 'financeiro', posicao: 80 },
    { chave: 'condicoes-pagamento', permissao: 'cadastros.ver', componente: CondicoesPagamento, grupo: 'financeiro', posicao: 70 },
  ],
  rotasExtras: [
    { caminho: 'parceiro/:id', componente: FichaParceiro },
    { caminho: 'empresas', componente: IrParaEmpresas },
    { caminho: 'clientes', componente: IrParaClientes },
    { caminho: 'fornecedores', componente: IrParaFornecedores },
    { caminho: 'contatos', componente: IrParaContatos },
  ],
  // CRM novo desenho, lote 6: contatos e empresas na busca da barra superior.
  buscaGlobal: buscaCadastros,
  // Ajustes do CRM (27/10, F1): o formulário de contato e empresa para a ficha da negociação abrir sem sair dela.
  formularioParceiro: FormularioParceiro,
  // CRM no novo desenho (lote 1, C-C): Empresas e Contatos do clone do RD, páginas do CRM na casca nova (sobre o mesmo cadastro).
  paginasCasca: [
    { chave: 'empresas', caminho: 'crm/empresas', componente: Empresas, permissao: 'cadastros.ver', pagina: 'cadastros.pessoas', icone: Building2, posicao: 40 },
    { chave: 'contatos', caminho: 'crm/contatos', componente: Contatos, permissao: 'cadastros.ver', pagina: 'cadastros.pessoas', icone: Contact, posicao: 50 },
  ],
  // Ajustes do CRM 27/10 (C6, K2, C8): empresa e contato no botão flutuante, no "Criar" e no formulário de negociação.
  criacoes: criacoesCadastros,
  i18n,
};

export default modulo;
